// Deterministic geometry check for a hand-drawn check-in signature (issue #121).
// Full rationale and the calibration fixtures: docs/CHECKIN_SIGNATURE_QUALITY.md.
// The Java twin is SignatureQuality.java; both judge the exact SVG string that is stored, and
// signatureQuality.fixtures.json pins them to the same verdicts. Change a number in both.
//
// Only geometry a renderer actually draws may count. The path is parsed with a strict grammar, every
// coordinate must lie inside the signature box (plus a pen radius of slack), a quadratic's control
// point is replaced by points sampled ON the curve, and every metric is measured on the FILLED pixels
// (all strokes in one path, nonzero winding, as an SVG renderer paints it), so zero-area or unrendered
// geometry never counts. The filled pixels are split into 8-connected components; ink is each component's
// marching-squares contour length, so anything that does not change a pixel cannot change the verdict.
//
// Thresholds (H = reference height, W = pen diameter):
//  - H = 230   the <Signature> box height in CSS px. Pointer points are CSS px, so this is
//              independent of devicePixelRatio; only the width varies between phone and desktop.
//  - W = 6     perfect-freehand `size`. The stored path is the closed OUTLINE of the ink, not its
//              centerline, so a dot is a ~6 px blob. Ink length of a component is therefore (contour - pi*W) / 2:
//              the pen blob is subtracted, and a dot counts as zero ink.
//  - Bounds    x in [-0.25*H, 5000], y in [-0.25*H, 1.25*H]. The client drops points outside the box,
//              so outline points sit at most a pen radius outside it; 5000 px is a generous width cap.
//              Anything else is INVALID before measuring (a renderer clips it away).
//  - Length    total outline length over 100,000 px is INVALID (a real signature is ~1-3k px); it bounds
//              the work of the scanline fill.
//  - TRIVIAL   extent < 0.12*H (~27.6 px) or ink length < 0.20*H (~46 px). Extent catches tiny
//              scribbles, ink length catches dots, dashes and scattered dots (large extent, no ink).
//  - Significant component: ink >= 0.05*H (~11.5 px); smaller ones are specks and are ignored by
//              the straight-line test so "line + a stray dot" is still a straight line.
//  - STRAIGHT  exactly one significant component whose shape is narrow across its principal axis
//              (B <= max(2.5*W, 0.12*A)) AND not much longer than it is wide (l <= 1.4*A). An arc
//              or loop raises B; a flat zig-zag raises l/A, so a flat one-stroke cursive does not warn. 1.4 is centred
//              on the measured gap: max line 1.05 vs min one-stroke cursive 1.83.
//  - Mask      more than 500,000 filled pixels is INVALID; an empty mask (nothing renders) is TRIVIAL.
//  - Fail closed: a non-finite metric is INVALID, never OK.

export const SIGNATURE_REFERENCE_HEIGHT = 230;
export const SIGNATURE_PEN_SIZE = 6;

const TRIVIAL_EXTENT = 0.12 * SIGNATURE_REFERENCE_HEIGHT;
const TRIVIAL_INK = 0.20 * SIGNATURE_REFERENCE_HEIGHT;
const SIGNIFICANT_INK = 0.05 * SIGNATURE_REFERENCE_HEIGHT;
const STRAIGHT_MIN_WIDTH_PENS = 2.5;
const STRAIGHT_WIDTH_RATIO = 0.12;
const STRAIGHT_INK_RATIO = 1.4;
const MAX_CHARS = 200000;
const MAX_POINTS = 50000;
const MIN_X = -0.25 * SIGNATURE_REFERENCE_HEIGHT;
const MAX_X = 5000;
const MIN_Y = -0.25 * SIGNATURE_REFERENCE_HEIGHT;
const MAX_Y = 1.25 * SIGNATURE_REFERENCE_HEIGHT;
const MAX_TOTAL_LENGTH = 100000;
const MAX_FILLED_PIXELS = 500000;

export type SignatureVerdict = 'EMPTY' | 'INVALID' | 'TRIVIAL' | 'STRAIGHT' | 'OK';

export interface SignatureMetrics {
    componentCount: number;
    significantComponents: number;
    /** max(width, height) of the bounding box of all on-curve outline points */
    extent: number;
    /** summed ink length of all rendered components (contour length, pen blob removed) */
    inkLength: number;
    /** longest-ink component: extent along / across its principal axis, and its ink length */
    majorExtent: number;
    minorExtent: number;
    componentInk: number;
}

const NUMBER_TOKEN = /^-?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/;

const EMPTY_METRICS: SignatureMetrics = {
    componentCount: 0, significantComponents: 0, extent: 0, inkLength: 0, majorExtent: 0, minorExtent: 0, componentInk: 0,
};

// Strict grammar (no commas; only space, tab, CR, LF separate tokens): exactly what getSvgPathFromStroke
// emits per stroke ("M x y Q x y x y ... Z", strokes concatenated without a separator), plus the legacy
// "M x y (L x y)+ Z". Anything else is INVALID, so a coordinate a renderer would never draw can never
// count as ink.
const TOKEN = /[ \t\r\n]+|[A-Za-z]|-?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?|[^]/g;
const COMMANDS = new Set(['M', 'Q', 'L', 'Z']);

interface Group { cmd: string; nums: number[] }

const inBox = (x: number, y: number): boolean => x >= MIN_X && x <= MAX_X && y >= MIN_Y && y <= MAX_Y;

/** Flat [x0, y0, x1, y1, ...] on-curve vertices per stroke, or null when the path is not acceptable. */
const parseStrokes = (svg: string): number[][] | null => {
    if (svg.length > MAX_CHARS) return null;
    const groups: Group[] = [];
    let numbers = 0;
    for (const token of svg.match(TOKEN) ?? []) {
        if (/^[ \t\r\n]+$/.test(token)) continue;
        if (/^[A-Za-z]$/.test(token)) {
            if (!COMMANDS.has(token)) return null;
            groups.push({ cmd: token, nums: [] });
        } else if (NUMBER_TOKEN.test(token)) {
            const n = Number(token);
            if (groups.length === 0 || !Number.isFinite(n) || ++numbers > 2 * MAX_POINTS) return null;
            groups[groups.length - 1].nums.push(n);
        } else {
            return null;
        }
    }

    const strokes: number[][] = [];
    let i = 0;
    while (i < groups.length) {
        if (groups[i].cmd !== 'M' || groups[i].nums.length !== 2) return null;
        const start = groups[i++].nums;
        if (!inBox(start[0], start[1])) return null;
        const pts = [start[0], start[1]];
        if (i >= groups.length) return null;
        if (groups[i].cmd === 'Q') {
            const q = groups[i++].nums;
            if (q.length === 0 || q.length % 4 !== 0) return null;
            for (let k = 0; k < q.length; k += 4) {
                if (!inBox(q[k], q[k + 1]) || !inBox(q[k + 2], q[k + 3])) return null;
                const x0 = pts[pts.length - 2];
                const y0 = pts[pts.length - 1];
                for (let s = 1; s <= 4; s++) {
                    const t = s / 4;
                    const u = 1 - t;
                    pts.push(u * u * x0 + 2 * u * t * q[k] + t * t * q[k + 2]);
                    pts.push(u * u * y0 + 2 * u * t * q[k + 1] + t * t * q[k + 3]);
                }
            }
        } else if (groups[i].cmd === 'L') {
            while (i < groups.length && groups[i].cmd === 'L') {
                const l = groups[i++].nums;
                if (l.length === 0 || l.length % 2 !== 0) return null;
                for (let k = 0; k < l.length; k += 2) {
                    if (!inBox(l[k], l[k + 1])) return null;
                    pts.push(l[k], l[k + 1]);
                }
            }
        } else {
            return null;
        }
        if (i >= groups.length || groups[i].cmd !== 'Z' || groups[i].nums.length !== 0) return null;
        i++;
        strokes.push(pts);
    }
    return strokes.length === 0 ? null : strokes;
};

const dist = (ax: number, ay: number, bx: number, by: number): number => {
    const dx = bx - ax;
    const dy = by - ay;
    return Math.sqrt(dx * dx + dy * dy);
};

/** Closed outline: every vertex to the next, the last back to the first. */
const outlineLength = (xy: number[]): number => {
    const n = xy.length / 2;
    let total = 0;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        total += dist(xy[2 * i], xy[2 * i + 1], xy[2 * j], xy[2 * j + 1]);
    }
    return total;
};

/** Pixel grid covering the bounding box of all vertices (plus a one pixel margin). */
interface Grid { x0: number; y0: number; w: number; h: number }

const gridFor = (strokes: number[][]): Grid => {
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const xy of strokes) {
        for (let i = 0; i < xy.length; i += 2) {
            if (xy[i] < minX) minX = xy[i];
            if (xy[i] > maxX) maxX = xy[i];
            if (xy[i + 1] < minY) minY = xy[i + 1];
            if (xy[i + 1] > maxY) maxY = xy[i + 1];
        }
    }
    const x0 = Math.floor(minX) - 1;
    const y0 = Math.floor(minY) - 1;
    return { x0, y0, w: Math.floor(maxX) + 2 - x0, h: Math.floor(maxY) + 2 - y0 };
};

/**
 * Fill mask of the given closed outlines, all in ONE path with the nonzero winding rule (what an
 * SVG renderer does for a single `d`). Pixel (px, py) is filled iff its centre (px+0.5, py+0.5)
 * has a nonzero winding number. An out-and-back outline has zero area and fills nothing.
 */
const fillMask = (outlines: number[][], g: Grid): Uint8Array => {
    const mask = new Uint8Array(g.w * g.h);
    const rows: number[][][] = Array.from({ length: g.h }, () => []);
    for (const xy of outlines) {
        const n = xy.length / 2;
        for (let i = 0; i < n; i++) {
            const j = (i + 1) % n;
            const ax = xy[2 * i], ay = xy[2 * i + 1], bx = xy[2 * j], by = xy[2 * j + 1];
            if (ay === by) continue;
            const dir = by > ay ? 1 : -1;
            const r1 = Math.ceil(Math.max(ay, by) - 0.5);
            for (let r = Math.ceil(Math.min(ay, by) - 0.5); r < r1; r++) {
                const gy = r - g.y0;
                if (gy < 0 || gy >= g.h) continue;
                const c = r + 0.5;
                rows[gy].push([ax + (c - ay) * (bx - ax) / (by - ay), dir]);
            }
        }
    }
    for (let gy = 0; gy < g.h; gy++) {
        const row = rows[gy];
        row.sort((p, q) => (p[0] - q[0]) || (p[1] - q[1]));
        let winding = 0;
        for (let k = 0; k < row.length; k++) {
            if (winding !== 0 && k > 0) {
                const last = Math.ceil(row[k][0] - 0.5);
                for (let px = Math.ceil(row[k - 1][0] - 0.5); px < last; px++) {
                    const gx = px - g.x0;
                    if (gx >= 0 && gx < g.w) mask[gy * g.w + gx] = 1;
                }
            }
            winding += row[k][1];
        }
    }
    return mask;
};

interface MaskStats { count: number; extent: number }

const maskStats = (mask: Uint8Array, g: Grid): MaskStats => {
    let count = 0, minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (let gy = 0; gy < g.h; gy++) {
        for (let gx = 0; gx < g.w; gx++) {
            if (mask[gy * g.w + gx] === 0) continue;
            count++;
            if (gx < minX) minX = gx;
            if (gx > maxX) maxX = gx;
            if (gy < minY) minY = gy;
            if (gy > maxY) maxY = gy;
        }
    }
    return { count, extent: count === 0 ? 0 : Math.max(maxX - minX + 1, maxY - minY + 1) };
};

/** Marching-squares contour length of one window; corners clockwise from top-left: TL=1, TR=2, BR=4, BL=8. */
const CONTOUR_LENGTH = [
    0, Math.SQRT2 / 2, Math.SQRT2 / 2, 1, Math.SQRT2 / 2, Math.SQRT2, 1, Math.SQRT2 / 2,
    Math.SQRT2 / 2, 1, Math.SQRT2, Math.SQRT2 / 2, 1, Math.SQRT2 / 2, Math.SQRT2 / 2, 0,
];

interface Component { pixels: number[]; contour: number }

/**
 * 8-connected components of the mask, found by a flood fill in row-major scan order. Each carries
 * the length of its own iso-0.5 contour (marching squares over a one pixel padded window grid),
 * which is exact for horizontal, vertical and 45 degree edges and cannot be changed by retracing.
 */
const components = (mask: Uint8Array, g: Grid): Component[] => {
    const label = new Int32Array(g.w * g.h);
    const stamp = new Int32Array((g.w + 1) * (g.h + 1));
    const result: Component[] = [];
    const stack: number[] = [];
    for (let start = 0; start < mask.length; start++) {
        if (mask[start] === 0 || label[start] !== 0) continue;
        const id = result.length + 1;
        const pixels: number[] = [];
        label[start] = id;
        stack.push(start);
        while (stack.length > 0) {
            const at = stack.pop()!;
            pixels.push(at);
            const ax = at % g.w, ay = (at - ax) / g.w;
            for (let dy = -1; dy <= 1; dy++) {
                for (let dx = -1; dx <= 1; dx++) {
                    const x = ax + dx, y = ay + dy;
                    if (x < 0 || x >= g.w || y < 0 || y >= g.h) continue;
                    const to = y * g.w + x;
                    if (mask[to] !== 0 && label[to] === 0) {
                        label[to] = id;
                        stack.push(to);
                    }
                }
            }
        }
        // Windows are identified by their top-left corner in a grid padded by one; each is visited once.
        const member = (x: number, y: number): number =>
            (x >= 0 && x < g.w && y >= 0 && y < g.h && label[y * g.w + x] === id) ? 1 : 0;
        let contour = 0;
        for (const at of pixels) {
            const px = at % g.w, py = (at - px) / g.w;
            for (let wy = py - 1; wy <= py; wy++) {
                for (let wx = px - 1; wx <= px; wx++) {
                    const key = (wy + 1) * (g.w + 1) + (wx + 1);
                    if (stamp[key] === id) continue;
                    stamp[key] = id;
                    contour += CONTOUR_LENGTH[member(wx, wy) | (member(wx + 1, wy) << 1)
                        | (member(wx + 1, wy + 1) << 2) | (member(wx, wy + 1) << 3)];
                }
            }
        }
        result.push({ pixels, contour });
    }
    return result;
};

/** Extent of pixel centres along and across their principal axis (closed-form 2x2 eigenvector). */
const principalExtents = (pixels: number[], g: Grid): [number, number] => {
    const n = pixels.length;
    if (n === 0) return [0, 0];
    let mx = 0, my = 0;
    for (const at of pixels) {
        const x = at % g.w;
        mx += x;
        my += (at - x) / g.w;
    }
    mx /= n;
    my /= n;
    let a = 0, b = 0, c = 0;
    for (const at of pixels) {
        const x = at % g.w;
        const dx = x - mx, dy = (at - x) / g.w - my;
        a += dx * dx; b += dx * dy; c += dy * dy;
    }
    let ux = 1;
    let uy = 0;
    if (b !== 0) {
        const lambda = (a + c) / 2 + Math.sqrt(((a - c) / 2) * ((a - c) / 2) + b * b);
        const len = Math.sqrt(b * b + (lambda - a) * (lambda - a));
        ux = b / len;
        uy = (lambda - a) / len;
    } else if (c > a) {
        ux = 0;
        uy = 1;
    }
    let uMin = Infinity, uMax = -Infinity, vMin = Infinity, vMax = -Infinity;
    for (const at of pixels) {
        const x = at % g.w;
        const dx = x - mx, dy = (at - x) / g.w - my;
        const u = dx * ux + dy * uy;
        const v = -dx * uy + dy * ux;
        if (u < uMin) uMin = u;
        if (u > uMax) uMax = u;
        if (v < vMin) vMin = v;
        if (v > vMax) vMax = v;
    }
    return [uMax - uMin + 1, vMax - vMin + 1];
};

export const analyzeSignatureSvg = (svg: string | null | undefined):
    { verdict: SignatureVerdict; metrics: SignatureMetrics } => {
    const invalid = { verdict: 'INVALID' as SignatureVerdict, metrics: EMPTY_METRICS };
    if (!svg || svg.trim().length === 0) return { verdict: 'EMPTY', metrics: EMPTY_METRICS };
    const strokes = parseStrokes(svg);
    if (!strokes) return invalid;

    let totalLength = 0;
    for (const xy of strokes) totalLength += outlineLength(xy);
    if (!(totalLength <= MAX_TOTAL_LENGTH)) return invalid;

    const grid = gridFor(strokes);
    const mask = fillMask(strokes, grid);
    const { count, extent } = maskStats(mask, grid);
    if (count > MAX_FILLED_PIXELS) return invalid;
    // Well formed but nothing renders (for example an out-and-back line with zero area).
    if (count === 0) return { verdict: 'TRIVIAL', metrics: EMPTY_METRICS };

    let inkLength = 0;
    let significant = 0;
    let longest: Component | null = null;
    let longestInk = -1;
    const parts = components(mask, grid);
    for (const part of parts) {
        const ink = Math.max(0, (part.contour - Math.PI * SIGNATURE_PEN_SIZE) / 2);
        inkLength += ink;
        if (ink >= SIGNIFICANT_INK) significant++;
        if (ink > longestInk) { longestInk = ink; longest = part; }
    }
    const [majorExtent, minorExtent] = principalExtents(longest!.pixels, grid);
    const metrics: SignatureMetrics = {
        componentCount: parts.length, significantComponents: significant, extent, inkLength,
        majorExtent, minorExtent, componentInk: longestInk,
    };
    if (![extent, inkLength, majorExtent, minorExtent, longestInk].every(Number.isFinite)) {
        return { verdict: 'INVALID', metrics };
    }

    if (extent < TRIVIAL_EXTENT || inkLength < TRIVIAL_INK) return { verdict: 'TRIVIAL', metrics };
    if (significant === 1
        && minorExtent <= Math.max(STRAIGHT_MIN_WIDTH_PENS * SIGNATURE_PEN_SIZE, STRAIGHT_WIDTH_RATIO * majorExtent)
        && longestInk <= STRAIGHT_INK_RATIO * majorExtent) {
        return { verdict: 'STRAIGHT', metrics };
    }
    return { verdict: 'OK', metrics };
};

/**
 * TRIVIAL, INVALID and EMPTY can never be submitted; STRAIGHT only once the guest confirmed
 * this exact drawing. Any new stroke changes the SVG, so an old confirmation never carries over.
 */
export const canSubmitSignature = (
    verdict: SignatureVerdict,
    confirmedSvg: string | null,
    currentSvg: string,
): boolean => {
    if (verdict === 'OK') return true;
    if (verdict === 'STRAIGHT') return confirmedSvg !== null && confirmedSvg === currentSvg;
    return false;
};
